(function () {
  'use strict';

  if (window.haptixVideoControllerInstalled) return;
  window.haptixVideoControllerInstalled = true;

  const states = new Map();
  let scheduledFrame = null;

  function isManaged(video) {
    return (
      video.hasAttribute('autoplay') ||
      video.hasAttribute('data-haptix-autoplay') ||
      Boolean(
        video.closest('.mts-wrap, .haptix-steps__media-item')
      )
    );
  }

  function automaticPlaybackEnabled(video) {
    const step = video.closest('.haptix-steps__media-item');

    if (step && step.hasAttribute('data-haptix-autoplay-enabled')) {
      return step.getAttribute('data-haptix-autoplay-enabled') === 'true';
    }

    if (video.closest('.mts-wrap')) return true;

    return (
      video.hasAttribute('autoplay') ||
      video.hasAttribute('data-haptix-autoplay')
    );
  }

  function isActive(video) {
    const slider = video.closest('.mts-slider-wrap');
    const card = video.closest('.mts-card');
    const step = video.closest('.haptix-steps__media-item');

    if (slider && !slider.classList.contains('active')) {
      return false;
    }

    if (card && !card.classList.contains('center')) {
      return false;
    }

    if (step && !step.classList.contains('is-active')) {
      return false;
    }

    return true;
  }

  function isVisible(video) {
    if (!video.isConnected || !video.getClientRects().length) {
      return false;
    }

    const rect = video.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) {
      return false;
    }

    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    const visibleWidth = Math.max(
      0,
      Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0)
    );

    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
    );

    const referenceArea =
      Math.min(rect.width, viewportWidth) *
      Math.min(rect.height, viewportHeight);

    return (
      referenceArea > 0 &&
      (visibleWidth * visibleHeight) / referenceArea >= 0.25
    );
  }

  function playbackAllowed(video) {
    return (
      !document.hidden &&
      isActive(video) &&
      isVisible(video)
    );
  }

  function shouldStartAutomatically(video, state) {
    return (
      state.autoplayEnabled &&
      !state.manuallyPaused &&
      playbackAllowed(video)
    );
  }

  function pauseAutomatically(video, state) {
    if (video.paused) return;

    state.automaticPausePending = true;
    video.pause();
  }

  function updateVideo(video, state) {
    if (!playbackAllowed(video)) {
      pauseAutomatically(video, state);
      return;
    }

    if (
      !shouldStartAutomatically(video, state) ||
      !video.paused ||
      state.playPending
    ) {
      return;
    }

    state.playPending = true;

    try {
      const attempt = video.play();

      if (attempt && typeof attempt.then === 'function') {
        attempt
          .then(function () {
            if (!playbackAllowed(video)) {
              pauseAutomatically(video, state);
            }
          })
          .catch(function () {
            // Browser oder Energiesparmodus kann Autoplay blockieren.
            // Keine sofortige Wiederholungsschleife.
          })
          .finally(function () {
            state.playPending = false;
          });
      } else {
        state.playPending = false;
      }
    } catch (error) {
      state.playPending = false;
    }
  }

  function registerVideo(video) {
    if (states.has(video) || !isManaged(video)) return;

    const controller = new AbortController();

    const state = {
      controller: controller,
      autoplayEnabled: automaticPlaybackEnabled(video),
      manuallyPaused: false,
      automaticPausePending: false,
      playPending: false
    };

    states.set(video, state);

    if (state.autoplayEnabled) {
      video.setAttribute('data-haptix-autoplay', '');
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
    }

    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.playsInline = true;
    video.setAttribute('playsinline', '');

    const options = { signal: controller.signal };

    video.addEventListener('pause', function () {
      if (state.automaticPausePending) {
        state.automaticPausePending = false;
        return;
      }

      // Eine bewusste Pause bleibt bestehen.
      state.manuallyPaused = true;
    }, options);

    video.addEventListener('play', function () {
      state.manuallyPaused = false;

      if (!playbackAllowed(video)) {
        pauseAutomatically(video, state);
      }
    }, options);

    video.addEventListener('loadeddata', scheduleUpdate, options);
    video.addEventListener('canplay', scheduleUpdate, options);

    if (intersectionObserver) {
      intersectionObserver.observe(video);
    }
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;

    if (root instanceof HTMLVideoElement) {
      registerVideo(root);
    }

    root.querySelectorAll('video').forEach(registerVideo);
  }

  function cleanupRemovedVideos() {
    states.forEach(function (state, video) {
      if (video.isConnected) return;

      if (intersectionObserver) {
        intersectionObserver.unobserve(video);
      }

      state.controller.abort();
      video.pause();
      states.delete(video);
    });
  }

  function updateAll() {
    scheduledFrame = null;
    cleanupRemovedVideos();

    states.forEach(function (state, video) {
      updateVideo(video, state);
    });
  }

  function scheduleUpdate() {
    if (scheduledFrame !== null) return;

    scheduledFrame = window.requestAnimationFrame(updateAll);
  }

  const intersectionObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(scheduleUpdate, {
          threshold: [0, 0.25, 0.5, 1]
        })
      : null;

  function start() {
    scan(document);

    const mutationObserver = new MutationObserver(function (mutations) {
      let needsUpdate = false;

      mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === 1) scan(node);
          });

          if (
            mutation.addedNodes.length ||
            mutation.removedNodes.length
          ) {
            needsUpdate = true;
          }

          return;
        }

        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof Element &&
          mutation.target.matches(
            '.mts-slider-wrap, .mts-card, .haptix-steps__media-item'
          )
        ) {
          needsUpdate = true;
        }
      });

      if (needsUpdate) scheduleUpdate();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    scheduleUpdate();
  }

  document.addEventListener('visibilitychange', scheduleUpdate);
  document.addEventListener('haptix:video-update', scheduleUpdate);

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
    scheduleUpdate();
  });

  document.addEventListener('shopify:section:unload', scheduleUpdate);

  window.addEventListener('scroll', scheduleUpdate, {
    passive: true
  });

  window.addEventListener('resize', scheduleUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();
